import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Package, Mail, Loader2, Calendar, RefreshCw, AlertCircle, Home, FileText, Upload, ExternalLink, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast, Toaster } from 'sonner';

const API = (() => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
})();

const WEBSITE_URL = (() => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz';
  }
  return window.location.origin;
})();

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [confirmationData, setConfirmationData] = useState(null);
  const [isGuest, setIsGuest] = useState(true);
  const [noPendingOrder, setNoPendingOrder] = useState(false);
  
  // Get order info from URL params or localStorage
  const orderId = searchParams.get('order_id');
  
  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('guestToken');
    if (token) {
      setIsGuest(false);
    }
    
    // Get pending order from localStorage
    const pendingOrder = localStorage.getItem('pending_order');
    if (pendingOrder) {
      try {
        setOrderDetails(JSON.parse(pendingOrder));
      } catch (e) {
        setNoPendingOrder(true);
      }
    } else {
      setNoPendingOrder(true);
    }
  }, []);

  // Determine product types in cart
  const getProductTypes = (cartItems) => {
    if (!cartItems) return { hasTradingView: false, hasBonbot: false, hasOther: false };
    
    const hasTradingView = cartItems.some(item => 
      item.product_id?.includes('tradingview') || 
      item.category === 'indicator' ||
      item.name?.toLowerCase().includes('indicator')
    );
    
    const hasBonbot = cartItems.some(item => 
      item.product_id?.includes('bonbot') || 
      item.category === 'signal_generator' ||
      item.name?.toLowerCase().includes('bonbot')
    );
    
    const hasOther = cartItems.some(item => 
      !item.product_id?.includes('tradingview') && 
      !item.product_id?.includes('bonbot') &&
      item.category !== 'indicator' &&
      item.category !== 'signal_generator'
    );
    
    return { hasTradingView, hasBonbot, hasOther };
  };

  const handleConfirmPayment = async () => {
    setLoading(true);
    
    try {
      const pendingOrder = JSON.parse(localStorage.getItem('pending_order') || '{}');
      const token = localStorage.getItem('guestToken');
      
      const res = await fetch(`${API}/payment/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: pendingOrder.email,
          order_id: pendingOrder.order_id,
          cart_items: pendingOrder.cart_items,
          total_amount: pendingOrder.total_amount,
          full_name: pendingOrder.full_name,
          phone: pendingOrder.phone,
          billing_address: pendingOrder.billing_address,
          trading_view_username: pendingOrder.trading_view_username,
          user_token: token || null
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        setConfirmed(true);
        setConfirmationData(data);
        setOrderDetails({
          ...pendingOrder,
          ...data.order
        });
        
        // Clear pending order and cart
        localStorage.removeItem('pending_order');
        localStorage.removeItem('bonbot_cart');
        
        toast.success('Payment confirmed! Check below for next steps.');
      } else {
        toast.error(data.message || 'Failed to confirm payment');
      }
    } catch (err) {
      toast.error('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get product-specific instructions
  const renderProductInstructions = () => {
    const cartItems = orderDetails?.cart_items || confirmationData?.order?.cart_items || [];
    const { hasTradingView, hasBonbot, hasOther } = getProductTypes(cartItems);
    
    return (
      <div className="space-y-4">
        {/* TradingView Indicator Instructions */}
        {hasTradingView && (
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="text-white font-bold">TradingView Indicator</h3>
                <Badge className="bg-purple-500/20 text-purple-300 text-xs">Order Received</Badge>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              <p className="text-gray-300">
                We have received your order! Our team will reach out to you via email within <strong className="text-purple-400">24-48 hours</strong> to:
              </p>
              <ul className="space-y-2 text-gray-300 ml-4">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                  <span>Activate your TradingView indicator access</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                  <span>Provide setup resources and installation guide</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                  <span>Answer any questions you may have</span>
                </li>
              </ul>
              <div className="bg-purple-500/10 rounded-lg p-3 mt-3">
                <p className="text-purple-300 text-xs">
                  📧 Keep an eye on your inbox (and spam folder) for activation email from our team.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Bonbot Signal Generator Instructions */}
        {hasBonbot && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                <Upload className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-bold">Bonbot Smart Trading Alert System</h3>
                <Badge className="bg-amber-500/20 text-amber-300 text-xs">Action Required</Badge>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              <p className="text-gray-300">
                Thank you for your purchase! To activate your Bonbot service:
              </p>
              <div className="bg-amber-500/20 border border-amber-500/40 rounded-lg p-4">
                <p className="text-amber-300 font-medium mb-2">📋 Next Step Required:</p>
                <p className="text-gray-300">
                  Please <strong className="text-amber-400">submit your Square invoice/receipt</strong> to our billing team to activate your service.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <a 
                  href="mailto:billing@bonbot.biz?subject=Bonbot Service Activation - Invoice Submission"
                  className="flex items-center justify-center gap-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 py-3 px-4 rounded-lg transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Email Receipt to billing@bonbot.biz
                </a>
                <Button
                  onClick={() => navigate('/portal')}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Go to Signal Generator
                </Button>
              </div>
              <p className="text-gray-400 text-xs">
                Once we verify your payment, your service will be activated within 24 hours.
              </p>
            </div>
          </div>
        )}

        {/* Other Services (Coaching, Mentorship) Instructions */}
        {hasOther && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="text-white font-bold">Coaching / Mentorship Service</h3>
                <Badge className="bg-blue-500/20 text-blue-300 text-xs">Processing</Badge>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              <p className="text-gray-300">
                Thank you for your purchase! You will receive an email with detailed instructions shortly.
              </p>
              <div className="bg-blue-500/10 rounded-lg p-4">
                <p className="text-blue-300 font-medium mb-2">📧 What to expect:</p>
                <ul className="space-y-1 text-gray-300 text-sm">
                  <li>• Scheduling instructions for your session</li>
                  <li>• Preparation materials</li>
                  <li>• Contact information for your coach/mentor</li>
                </ul>
              </div>
              <p className="text-gray-300">
                In the meantime, you can set up your account to access additional resources.
              </p>
            </div>
          </div>
        )}

        {/* Account Setup CTA */}
        {isGuest && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="text-white font-bold">Set Up Your Account</h3>
                <Badge className="bg-green-500/20 text-green-300 text-xs">Recommended</Badge>
              </div>
            </div>
            <p className="text-gray-300 text-sm mb-3">
              Check your email at <strong className="text-green-400">{orderDetails?.email}</strong> for a link to create your account and access all your services.
            </p>
            <div className="text-xs text-gray-400">
              Didn't receive the email? Check your spam folder or contact worood@forthewisepro.com
            </div>
          </div>
        )}

        {/* Website Link */}
        <div className="bg-slate-800/50 rounded-lg p-4 text-center">
          <p className="text-gray-400 text-sm mb-2">Access your services anytime at:</p>
          <a 
            href={WEBSITE_URL}
            className="text-blue-400 hover:text-blue-300 font-medium flex items-center justify-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            {WEBSITE_URL.replace('https://', '')}
          </a>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Toaster position="top-right" richColors />
      
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg p-8">
        {/* No Pending Order State */}
        {noPendingOrder && !confirmed ? (
          <>
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-amber-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">No Pending Order</h1>
              <p className="text-gray-400">
                We couldn't find a pending order. If you recently made a payment, please contact support.
              </p>
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500"
              >
                <Home className="w-4 h-4 mr-2" />
                Return to Homepage
              </Button>
              <a 
                href="mailto:worood@forthewisepro.com" 
                className="block w-full text-center text-blue-400 hover:text-blue-300 text-sm"
              >
                Contact Support: worood@forthewisepro.com
              </a>
              <div className="text-center pt-2">
                <a href={WEBSITE_URL} className="text-gray-500 text-xs hover:text-gray-400">
                  {WEBSITE_URL}
                </a>
              </div>
            </div>
          </>
        ) : !confirmed ? (
          <>
            {/* Pre-confirmation State */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Payment Complete?</h1>
              <p className="text-gray-400">
                If you've completed your payment on Square, click below to confirm and receive your service instructions.
              </p>
            </div>

            {/* Order Summary */}
            {orderDetails && (
              <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-medium text-gray-400 mb-3">Your Order:</h3>
                <div className="space-y-2">
                  {orderDetails.cart_items?.map((item, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-white">{item.name}</span>
                      <span className="text-green-400">${item.price?.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t border-white/10 pt-2 mt-2">
                    <div className="flex justify-between items-center font-bold">
                      <span className="text-white">Total</span>
                      <span className="text-green-400">${orderDetails.total_amount?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-white/10">
                  <p className="text-xs text-gray-500">Order ID: {orderDetails.order_id}</p>
                  <p className="text-xs text-gray-500">Email: {orderDetails.email}</p>
                </div>
              </div>
            )}

            {/* Confirm Button */}
            <Button
              onClick={handleConfirmPayment}
              disabled={loading}
              data-testid="done-with-payment-btn"
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white py-6 text-lg font-bold"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Confirming...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Done with Payment
                </>
              )}
            </Button>

            <p className="text-gray-500 text-sm text-center mt-4">
              Only click if you've completed payment on Square
            </p>
            
            <div className="text-center pt-4">
              <a href={WEBSITE_URL} className="text-gray-500 text-xs hover:text-gray-400">
                {WEBSITE_URL}
              </a>
            </div>
          </>
        ) : (
          <>
            {/* Post-confirmation State */}
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Order Confirmed!</h1>
              <p className="text-gray-400 text-sm">
                Order #{orderDetails?.order_id || confirmationData?.order?.order_id}
              </p>
            </div>

            {/* Product-Specific Instructions */}
            {renderProductInstructions()}

            {/* Action Buttons */}
            <div className="space-y-3 mt-6">
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500"
              >
                <Home className="w-4 h-4 mr-2" />
                Return to Homepage
              </Button>
              <a 
                href="mailto:worood@forthewisepro.com" 
                className="block w-full text-center text-gray-400 hover:text-gray-300 text-sm"
              >
                Need help? Contact worood@forthewisepro.com
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
